import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useLocation, useParams} from 'react-router-dom';
import {useAuth} from "../../../providers/AuthenticationProvider";
import {Form, Checkbox, Button, Loader, Dimmer, Segment} from "semantic-ui-react";
import {equipmentService} from '../../../services/equipmentService'
import {TreeView} from "@progress/kendo-react-treeview";
import {useCustomers} from "../../../providers/CustomerProvider";
import {useDataInspector} from "../../../providers/DataInspectorProvider";
import {SemanticToastContainer, toast} from "react-semantic-toasts";
import {pathianDataApiService} from "../../../services/PathianDataApiService";

export const EquipmentEdit = () => {
    const {id} = useParams();
    const { checkAuth, getUserType } = useAuth();
    const navigate = useNavigate();
    const browserLocation = useLocation();

    const [equipmentType, setEquipmentType] = useState('Equipment');
    const {customersList, getAllCustomers, selectedCustomer} = useCustomers();
    const {buildings, getDataInspectorBuildings} = useDataInspector();
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [buildingSelected, setBuildingSelected] = useState(null);
    const [customerId, setCustomerId] = useState(null);
    const [scheduleTree, setScheduleTree] = useState([]);
    const [selectedEquipmentScheduleTree, setSelectedEquipmentScheduleTree] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [location, setLocation] = useState("");
    const [manufacturer, setManufacturer] = useState("");
    const [checkedItems, setCheckedItems] = useState({});
    const [isLoading, setIsLoading] = useState(!!id);
    const [equipmentDataLoaded, setEquipmentDataLoaded] = useState(false);
    const [buildingsLoaded, setBuildingsLoaded] = useState(false);
    const [scheduleTreeLoaded, setScheduleTreeLoaded] = useState(false);
    const navTimerRef = useRef(null);

    checkAuth(getUserType(), 'Equipment Edit') && navigate('/');

    useEffect(() => () => clearTimeout(navTimerRef.current), []);

    useEffect(() => {
        getAllCustomers();
    }, [getAllCustomers]);

    useEffect(() => {
        if (!id) return;
        equipmentService.getEquipmentById(id).then(response => {
            handleCustomerChange(response.data.groupId);
            setName(response.data.name);
            setDescription(response.data.description);
            setLocation(response.data.location);
            setManufacturer(response.data.manufacturer);
            setSelectedEquipment(response.data.componentId);
            setBuildingSelected(response.data.parentId);
            setEquipmentDataLoaded(true);
        });
    }, [id]);

    useEffect(() => {
        if (browserLocation.pathname.includes('building'))
        {
            setEquipmentType('Building');
            setSelectedEquipment('0957bfa9-2041-4d2d-b003-187637f7cfe8'); //building component_id
        }
        if (browserLocation.pathname.includes('space'))
        {
            setEquipmentType('Space');
            setSelectedEquipment('dc6b381b-0ad3-4dcc-922a-003d6f2d6887'); //space component_id
        }
    }, [browserLocation]);

    useEffect(() => {
        if (customersList.length > 0 && selectedCustomer !== null) {
            const groupId = customersList.find(x => x.id === selectedCustomer)?.groupId;
            setCustomerId(groupId);
            if (groupId) {
                getDataInspectorBuildings(groupId);
            }
        }
    }, [customersList, selectedCustomer])

    useEffect(() => {
        let tempOptions = [];
        setBuildingOptions([]);
        (buildings || []).forEach(equipmentItem => {
            tempOptions.push({
                key: equipmentItem.id,
                value: equipmentItem.id,
                text: `(${equipmentItem.id}) ${equipmentItem.name}`,
            });
        });
        setBuildingOptions(tempOptions);
        if (equipmentDataLoaded) {
            setBuildingsLoaded(true);
        }
    }, [buildings, equipmentDataLoaded]);

    async function getScheduleTree(componentId) {
        return await equipmentService.getScheduleTree(componentId);
    }

    async function loadChildren(categoryIndex, categoryId) {
        const { data, category } = copyDataAndCategory(categoryIndex);
        category.items = await equipmentService.getScheduleTree(categoryId);

        //remove "None" options as they aren't needed for this driver implementation
        const noneIndex = category.items.findIndex(x => x.id === 'ce4d2c16-cbe0-4edf-9aa7-3edc7da8de5e');
        if (noneIndex !== -1) {
            category.items.splice(noneIndex, 1);
        }

        setSelectedEquipmentScheduleTree(data);
    }

    useEffect( () => {
        //fetch root "Equipment" section of the schedule tree
        getScheduleTree('ccae1562-3e8b-429a-adde-580cbd2e2d8f').then(response => {
            setScheduleTree(Array.isArray(response) ? response : []);
        })
    }, []);

    useEffect(() => {
        if (!selectedEquipment) {
            return;
        }
        getScheduleTree(selectedEquipment).then(response => {
            setSelectedEquipmentScheduleTree(Array.isArray(response) ? response : []);
            if (id) setScheduleTreeLoaded(true);
        });
    }, [selectedEquipment]);

    useEffect(() => {
        if (!id || !equipmentDataLoaded || !buildingsLoaded) return;
        if (equipmentType === 'Equipment' && !scheduleTreeLoaded) return;
        setIsLoading(false);
    }, [id, equipmentDataLoaded, buildingsLoaded, scheduleTreeLoaded, equipmentType]);

    async function onExpandChange(event) {
        const categoryIndex = event.itemHierarchicalIndex.split('_').map(Number);
        const { data, category } = copyDataAndCategory(categoryIndex);
        category.expanded = !category.expanded;

        setSelectedEquipmentScheduleTree(data);
        !category.items && loadChildren(categoryIndex, category.id);
    }

    function copyDataAndCategory(categoryIndex) {
        const data = selectedEquipmentScheduleTree.slice();
        const category = traverseTree(data, categoryIndex);
        return {
            data,
            category
        };
    }

    function traverseTree(data, categoryIndex) {
        const category = data[categoryIndex[0]];
        if (categoryIndex.length > 1) {
            return traverseTree(category.items, categoryIndex.slice(1));
        }
        return category;
    }

    function onCheckChange(event, item) {
        if (item.id in checkedItems) {
            delete checkedItems[item.id];
        }
        else {
            checkedItems[item.id] = item.description;
        }
    }

    async function createEquipment() {
        if (!customerId) {
            toast({
                type: 'error',
                description: 'Cannot create equipment: the selected customer does not have a valid group configured.',
                animation: 'fade down',
                time: 5000,
            });
            return;
        }
        const equipment = {
            component_id: selectedEquipment,
            parent_id: buildingSelected,
            name,
            description,
            location,
            manufacturer,
            subcomponents: Object.keys(checkedItems).map(key => ({
                component_id: key,
                name: checkedItems[key]
            }))
        }
        //space specific logic
        if (equipment.component_id === 'dc6b381b-0ad3-4dcc-922a-003d6f2d6887') {
            if (equipment.subcomponents.length !== 1) {
                toast({
                    type: 'error',
                    description: 'Please select exactly one type to associate with this Space.',
                    animation: 'fade down',
                    time: 5000,
                });
                return;
            }
            equipment.component_id = equipment.subcomponents[0].component_id;
            equipment.subcomponents = [];
        }
        const body = {
            equipment: [equipment],
            groupId: customerId
        }
        const response = await pathianDataApiService.createEquipment(body);
        if (response.error !== undefined) {
            toast({
                type: 'error',
                description: response.message,
                animation: 'fade down',
                time: 0,
            });
        } else {
            toast({
                type: 'success',
                description: `Created Equipment ${response.equipments[0].name} (ID: ${response.equipments[0].id})`,
                animation: 'fade down',
                time: 3000,
            });
            navTimerRef.current = setTimeout(() => {
                navigate('/point-mapping', {
                    state: {
                        groupId: customerId,
                        buildingId: equipmentType === 'Building' ? parseInt(response.equipments[0].id) : buildingSelected,
                        equipmentId: equipmentType === 'Building' ? null : parseInt(response.equipments[0].id),
                        equipmentType: equipmentType
                    }
                });
            }, 3500);
        }
    }

    async function updateEquipment() {
        const equipment = {
            id,
            parentId: buildingSelected,
            name,
            description,
            location,
            manufacturer
        }
        const response = await equipmentService.updateEquipment(equipment);
        if (response.error !== undefined) {
            toast({
                type: 'error',
                description: response.message,
                animation: 'fade down',
                time: 0,
            });
        } else {
            toast({
                type: 'success',
                description: `${equipmentType} Updated`,
                animation: 'fade down',
                time: 3000,
            });
            navTimerRef.current = setTimeout(() => navigate(-1), 3500);
        }
    }

    const handleCustomerChange = customerId => {
        setCustomerId(customerId);
        setBuildingSelected(null);
        setBuildingOptions([]);
        if (!customerId) {
            toast({
                type: 'warning',
                description: 'The selected customer does not have a valid group configured.',
                animation: 'fade down',
                time: 5000,
            });
            return;
        }
        getDataInspectorBuildings(customerId);
    };

    useEffect(() => {
        if (buildings.length === 1) {
            setBuildingSelected(buildings[0].id);
        }
    }, [buildings]);

    const CustomItem = (props) => {
        const isLeafNode = props.item.childCount === 0;
        return (
            <div style={{display: 'flex', marginLeft: isLeafNode ? '-24px' : ''}}>
                <Checkbox
                    hidden={!isLeafNode}
                    onChange={(event) => onCheckChange(event, props.item)}
                    style={{marginRight: '10px'}}
                    checked={props.item.id in checkedItems}
                />
                <span>{props.item.description}</span>
            </div>
        );
    }

    return (
        <div>
            <div className='pageHeader'>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <Button icon="arrow left" onClick={() => navigate(-1)} />
                    <h1>{!id ? 'Create' : 'Edit'} {equipmentType}</h1>
                </div>
            </div>
            {isLoading && (
                <Segment style={{ minHeight: '200px', border: 'none', boxShadow: 'none' }}>
                    <Dimmer active inverted>
                        <Loader>Loading...</Loader>
                    </Dimmer>
                </Segment>
            )}
            <Form
                style={{ maxWidth: '800px', display: isLoading ? 'none' : undefined }}
                id={'equipmentForm'}
                onSubmit={!id ? createEquipment : updateEquipment}>
                <Form.Group>
                    <Form.Dropdown
                        label={'Customer'}
                        width={16}
                        fluid
                        selection
                        search
                        options={customersList.map(x => {
                            const text =
                                x?.firstname && x?.lastname
                                    ? `${x?.companyName} (${x?.firstname} ${x?.lastname})`
                                    : x?.companyName;
                            return {
                                key: x?.groupId,
                                text: text,
                                value: x?.groupId,
                            };
                        })}
                        value={customerId}
                        style={{height: '38px'}}
                        onChange={(e, selected) => {
                            handleCustomerChange(selected.value);
                        }}
                        required
                        disabled={id}
                    />
                </Form.Group>
                {equipmentType === 'Equipment' && (
                    <Form.Group widths="equal">
                        <Form.Dropdown
                            label={'Equipment Type'}
                            required
                            fluid
                            search
                            selection
                            value={selectedEquipment}
                            options={scheduleTree.map((equipment) => ({
                                key: equipment.id,
                                value: equipment.id,
                                text: equipment.description
                            }))}
                            onChange={(e, {value}) => setSelectedEquipment(value)}
                            disabled={id}
                            style={{height: '38px'}}
                        />
                        <Form.Dropdown
                            label="Parent Building"
                            fluid
                            options={buildingOptions}
                            value={buildingSelected}
                            onChange={(e, {value}) => setBuildingSelected(value)}
                            selection
                            search
                            clearable
                            style={{height: '38px'}}
                        />
                    </Form.Group>
                )}
                {equipmentType === 'Space' && (
                    <Form.Group>
                        <Form.Dropdown
                            label="Parent Building"
                            width={16}
                            fluid
                            options={buildingOptions}
                            value={buildingSelected}
                            onChange={(e, {value}) => setBuildingSelected(value)}
                            selection
                            search
                            clearable
                            style={{height: '38px'}}
                        />
                    </Form.Group>
                )}
                <Form.Group widths="equal">
                    <Form.Input
                        label={'Name'}
                        required
                        value={name}
                        onChange={(e, {value}) => setName(value)}
                    />
                    <Form.Input
                        label={'Description'}
                        value={description}
                        onChange={(e, {value}) => setDescription(value)}
                    />
                </Form.Group>
                {equipmentType === 'Equipment' ? (
                    <Form.Group widths="equal">
                        <Form.Input
                            label={'Location'}
                            value={location}
                            onChange={(e, {value}) => setLocation(value)}
                        />
                        <Form.Input
                            label={'Manufacturer'}
                            value={manufacturer}
                            onChange={(e, {value}) => setManufacturer(value)}
                        />
                    </Form.Group>
                ) : (
                    <Form.Group>
                        <Form.Input
                            width={16}
                            label={'Location'}
                            value={location}
                            onChange={(e, {value}) => setLocation(value)}
                        />
                    </Form.Group>
                )}
                {(equipmentType === 'Equipment' || (equipmentType === 'Space' && !id)) && (
                    <TreeView
                        data={selectedEquipmentScheduleTree}
                        textField="description"
                        expandIcons={true}
                        hasChildrenField={"childCount"}
                        onExpandChange={onExpandChange}
                        item={CustomItem}
                    />
                )}
                <Form.Group>
                    <Form.Button
                        color={'blue'}
                        size={'large'}
                        style={{ marginTop: '32px' }}
                        type={'submit'}
                        form={'equipmentForm'}
                    >{!id ? 'Create' : 'Update'} {equipmentType}</Form.Button>
                </Form.Group>
            </Form>
            <SemanticToastContainer position="bottom-center"/>
        </div>
    );
}